import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faseDesde, revisarArchivo, type Fase } from './carga';

function pesoLegible(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.round(bytes / 1024)} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Zona de carga de una versión nueva.
 *
 * Dos decisiones que no son cosméticas:
 *
 * 1. **Debajo del área que se arrastra hay un `<input type="file">` de verdad.**
 *    No está para adornar: arrastrar no es una opción para quien navega con
 *    teclado o con lector de pantalla, y una zona de soltar hecha sólo con
 *    `div`s deja a esa gente sin forma de cargar nada. El input se oculta a la
 *    vista, no a la accesibilidad.
 *
 * 2. **La barra no se queda en 100 %.** Cuando el archivo termina de viajar, el
 *    servidor todavía tiene que escanearlo con antivirus y contar sus páginas —
 *    varios segundos en un PDF grande. Una barra llena y una pantalla congelada
 *    se leen como «se colgó»; por eso al llegar al final la fase cambia y lo
 *    dice: está procesando, no terminado.
 */
export function CargaDeVersion({
  ocupado,
  onSubir,
}: {
  ocupado: boolean;
  /** Sube el archivo informando del avance. Resuelve cuando el servidor terminó. */
  onSubir: (archivo: File, alAvanzar: (porcentaje: number) => void) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [fase, setFase] = useState<Fase>({ estado: 'quieto' });
  const [encima, setEncima] = useState(false);
  const [rechazo, setRechazo] = useState<string | null>(null);
  const entradaRef = useRef<HTMLInputElement>(null);
  // `dragleave` salta también al pasar sobre un hijo. Contar entradas y salidas
  // evita que el recuadro parpadee mientras el cursor sigue dentro.
  const profundidad = useRef(0);

  const trabajando = fase.estado !== 'quieto';

  function revisar(archivo: File): string | null {
    const rechazo = revisarArchivo(archivo);
    if (!rechazo) return null;
    return rechazo === 'noEsPdf'
      ? t('documents.uploadNotPdf')
      : rechazo === 'vacio'
        ? t('documents.uploadEmpty')
        : t('documents.uploadTooLarge', { size: pesoLegible(archivo.size) });
  }

  async function aceptar(archivo: File | undefined) {
    if (!archivo || trabajando || ocupado) return;
    const problema = revisar(archivo);
    setRechazo(problema);
    if (problema) return;

    setFase({ estado: 'subiendo', porcentaje: 0, nombre: archivo.name });
    try {
      await onSubir(archivo, (porcentaje) => setFase(faseDesde(porcentaje, archivo.name)));
    } finally {
      setFase({ estado: 'quieto' });
      if (entradaRef.current) entradaRef.current.value = '';
      profundidad.current = 0;
      setEncima(false);
    }
  }

  return (
    <div className="carga">
      <div
        className={`carga__zona${encima ? ' carga__zona--encima' : ''}${
          trabajando ? ' carga__zona--trabajando' : ''
        }`}
        onDragEnter={(e) => {
          e.preventDefault();
          profundidad.current += 1;
          setEncima(true);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={(e) => {
          e.preventDefault();
          profundidad.current -= 1;
          if (profundidad.current <= 0) setEncima(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          profundidad.current = 0;
          setEncima(false);
          void aceptar(e.dataTransfer.files?.[0]);
        }}
      >
        {trabajando ? (
          <div className="carga__avance">
            <span className="carga__archivo">{fase.nombre}</span>
            <div
              className="carga__barra"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              {...(fase.estado === 'subiendo' ? { 'aria-valuenow': fase.porcentaje } : {})}
              aria-label={t('documents.uploadProgress')}
            >
              <span
                className={`carga__relleno${
                  fase.estado === 'procesando' ? ' carga__relleno--indeterminado' : ''
                }`}
                style={
                  fase.estado === 'subiendo' ? { width: `${fase.porcentaje}%` } : { width: '100%' }
                }
              />
            </div>
            <span className="carga__estado" role="status">
              {fase.estado === 'subiendo'
                ? t('documents.uploadSending', { percent: fase.porcentaje })
                : t('documents.uploadScanning')}
            </span>
          </div>
        ) : (
          <>
            <span className="carga__invitacion">{t('documents.uploadDrop')}</span>
            <label className="boton boton--discreto carga__boton" htmlFor="archivo">
              {t('documents.uploadPick')}
            </label>
            <span className="carga__ayuda">{t('documents.uploadHelp')}</span>
          </>
        )}

        <input
          ref={entradaRef}
          id="archivo"
          className="carga__entrada"
          type="file"
          accept="application/pdf"
          disabled={trabajando || ocupado}
          onChange={(e) => void aceptar(e.target.files?.[0])}
        />
      </div>

      {rechazo && (
        <p className="carga__rechazo" role="alert">
          {rechazo}
        </p>
      )}
    </div>
  );
}
